import Phaser from 'phaser';

import { BlackjackMainScene } from '../../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../../model/resize-metrics';
import { ActionBus, BlackjackEvents, GameEvent } from 'game-logic';
import {
  AvailableFontColors,
  AvailableFontSizes,
  TextService,
} from '../../../services/text-service';
import { InterfaceEvents } from '../../../services/game-services';
import { ChipButtonControlView } from './chip-button-control-view';
import { ChipPickerViewModel } from './chip-picker-view-model';
import { RoundedNumberView } from '../../components/rounded-number-view';
import { UxMetrics } from '../../../model/ux-metrics';

export type ChipActions = {
  onDragStart: { pointer: Phaser.Input.Pointer; dragX: number; dragY: number };
  onDrag: { pointer: Phaser.Input.Pointer; dragX: number; dragY: number };
  onDragEnd: { pointer: Phaser.Input.Pointer; dragX: number; dragY: number };
};

function alignToMultiple(value: number, multiple: number): number {
  return Math.ceil(value / multiple) * multiple;
}

export class ChipPickerView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;
  private chipActionBus: ActionBus<ChipActions>;
  private maskGraphics: Phaser.GameObjects.Graphics;

  private scrollOffset?: number;
  private maxOffset = 0;
  private dragOffset = 0;
  private isDragging = false;
  private startX = 0;
  private snapTween?: Phaser.Tweens.Tween;
  private fadeValue = 0;

  private placeBetLabel: Phaser.GameObjects.Text;
  private innerContainer: Phaser.GameObjects.Container;
  private balanceLabel: RoundedNumberView;

  private chipAmountButtons: ChipButtonControlView[];

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: ChipPickerViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();
    this.chipActionBus = new ActionBus<ChipActions>();

    this.placeBetLabel = this.scene.add
      .text(
        0,
        0,
        viewModel.placeYourBetText,
        TextService.fontStyleBold(
          AvailableFontSizes.Medium,
          AvailableFontColors.Offwhite,
          this.viewModel.defaultFontFamily,
        ),
      )
      .setOrigin(0.5, 0.0);
    this.add(this.placeBetLabel);

    this.innerContainer = scene.add.container(0, 0);
    this.add(this.innerContainer);

    this.chipAmountButtons = this.viewModel.chipAmountViewModels.map((chipAmountViewModel) => {
      const chipAmountButton = new ChipButtonControlView(
        scene,
        0,
        0,
        chipAmountViewModel,
        this.chipActionBus,
      );
      this.innerContainer.add(chipAmountButton);
      return chipAmountButton;
    });

    this.balanceLabel = new RoundedNumberView(scene, 0, 0, viewModel.balanceViewModel);
    this.add(this.balanceLabel);

    // Don't add mask graphics into the scene otherwise the masking breaks
    this.maskGraphics = scene.make.graphics();
    this.setMask(this.maskGraphics.createGeometryMask());

    this.setName(ChipPickerView.name);
    scene.add.existing(this);

    this.handleInput();

    this.setupProgress(0);

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.scene.tweens.add({
          targets: this,
          fadeValue: 1,
          duration: UxMetrics.LoadingFade,
          ease: Phaser.Math.Easing.Quadratic.InOut,
          onUpdate: (_tween) => {
            this.setupProgress(this.fadeValue);
          },
        });
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onResetGameScene$.subscribe(() => {
        this.setupProgress(0);
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.showControl$.subscribe((showControl) => {
        this.setVisible(showControl);
        if (showControl) {
          this.viewModel.animateChipButtons(true);
        }
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
    this.subscriptionGroup.add(
      viewModel.interfaceEventQueue.subscribe(this.handleInterfaceEvents.bind(this)),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.chipAmountButtons.forEach((chipAmountButton) => chipAmountButton.onResize(resizeMetrics));
    this.balanceLabel?.onResize(resizeMetrics);

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private setupProgress(progress: number): void {
    this.setAlpha(progress);
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics || this.chipAmountButtons.length === 0) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    this.placeBetLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);

    const buttonWidth = this.chipAmountButtons[0].displayWidth;
    const buttonHeight = this.chipAmountButtons[0].displayHeight;

    const controlsWidth =
      (layoutConfig.boardWidth - layoutConfig.paddingSm * 2) * scalingMetrics.scaleFactor;

    const smSpacing = layoutConfig.paddingSm * scalingMetrics.scaleFactor;
    const lgSpacing = layoutConfig.paddingLg * scalingMetrics.scaleFactor;

    const totalHeight =
      this.placeBetLabel.displayHeight +
      buttonHeight +
      this.balanceLabel.displayHeight +
      smSpacing +
      lgSpacing;

    const xPos =
      layoutConfig.boardWidth * 0.5 * scalingMetrics.scaleFactor + scalingMetrics.scaledOffsetX;
    const yPos = layoutConfig.boardHeight * scalingMetrics.scaleFactor - totalHeight * 0.5;

    this.innerContainer.setSize(controlsWidth, buttonHeight);

    this.setSize(controlsWidth, totalHeight);
    this.setPosition(xPos, yPos);

    this.placeBetLabel.setY(this.placeBetLabel.displayHeight * 0.5 - totalHeight * 0.5);

    let accumHeight = this.placeBetLabel.displayHeight + lgSpacing;
    this.innerContainer.setY(buttonHeight * 0.5 + accumHeight - totalHeight * 0.5);

    accumHeight += buttonHeight + smSpacing;
    this.balanceLabel.setY(this.balanceLabel.displayHeight * 0.5 + accumHeight - totalHeight * 0.5);

    const totalChipWidth = this.layoutChipAmounts();

    const offset = (totalChipWidth - controlsWidth) * 0.5;
    this.maxOffset = offset > 0 ? alignToMultiple(offset, buttonWidth) : 0;

    if (this.scrollOffset === undefined) {
      this.scrollOffset = -this.maxOffset;
      this.innerContainer.x = this.scrollOffset;
    }

    this.drawMask();
  }

  public layoutChipAmounts(): number {
    const spacing = 0;

    const visibleButtons = this.chipAmountButtons.filter((button) => button.visible);

    let totalWidth = visibleButtons.reduce((curTotal, button) => curTotal + button.displayWidth, 0);
    totalWidth += (visibleButtons.length - 1) * spacing;

    let accumWidth = 0;
    for (const button of visibleButtons) {
      button.setPosition(button.displayWidth * 0.5 + accumWidth - totalWidth * 0.5, 0);
      accumWidth += button.displayWidth + spacing;
    }

    return totalWidth;
  }

  private drawMask(): void {
    this.maskGraphics.clear(); // Clear previous drawings

    const worldPoint = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(0, 0, worldPoint);

    // Define the "eraser" mask
    this.maskGraphics.fillStyle(0x0000ff); // Fill color doesn’t matter
    this.maskGraphics.fillRect(
      worldPoint.x + this.width * -0.5,
      worldPoint.y + this.height * -0.5,
      this.width,
      this.height,
    );
  }

  private handleInput(): void {
    this.chipActionBus.on('onDragStart', (payload) => {
      this.isDragging = true;
      this.startX = payload.pointer.x;
      if (this.snapTween) {
        this.snapTween.stop(); // Stop any existing snapping
      }
    });
    this.chipActionBus.on('onDrag', (payload) => {
      if (!this.isDragging) return;

      const currentOffset = this.scrollOffset ?? 0;
      const deltaX = payload.pointer.x - this.startX;

      this.dragOffset = currentOffset + deltaX;
      this.innerContainer.x = this.dragOffset;
    });
    this.chipActionBus.on('onDragEnd', (_payload) => {
      if (!this.isDragging) {
        return;
      }
      this.isDragging = false;

      const buttonWidth = Math.max(1, this.chipAmountButtons[0].displayWidth);
      this.scrollOffset = Math.round(this.innerContainer.x / buttonWidth) * buttonWidth;
      this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset, -this.maxOffset, this.maxOffset);

      this.snapTween = this.scene.tweens.add({
        targets: this.innerContainer,
        x: this.scrollOffset,
        duration: 300,
        ease: Phaser.Math.Easing.Back.Out,
      });
    });
  }

  private async handleLogicEvents(event: GameEvent<BlackjackEvents>): Promise<void> {
    // Don't process instant events
    if (event.instant) {
      return;
    }

    switch (event.type) {
      case 'onRoundStateChanged':
        this.viewModel.roundStateChanged(event.payload.state);
        break;
      case 'onMoveToNextRound':
        this.viewModel.resetChipAnimation();
        break;
      case 'onBankChanged':
        {
          this.viewModel.setBalanceAmount(event.payload.value);

          this.chipAmountButtons.forEach((chipAmountButton) => {
            const canDisplay = chipAmountButton.canDisplayForAmount(event.payload.value);
            chipAmountButton.setVisible(canDisplay);
            chipAmountButton.setAlpha(1.0);
          });
          this.scrollOffset = undefined;
          this.refreshLayout();
        }
        break;
      default:
        break;
    }
  }

  private async handleInterfaceEvents(event: GameEvent<InterfaceEvents>): Promise<void> {
    switch (event.type) {
      case 'onBetCleared':
      case 'onBetIncreased':
      case 'onBetAllIn':
        {
          const currentBet = event.payload.totalBetAmount;
          const currentBalance = event.payload.currentBalance;
          const remainingBalance = Phaser.Math.Clamp(
            currentBalance - currentBet,
            0,
            currentBalance,
          );
          const hasBetAmount = currentBet > 0;

          this.placeBetLabel.setVisible(!hasBetAmount);

          this.chipAmountButtons.forEach((chipAmountButton) => {
            const isEnabled = chipAmountButton.canDisplayForAmount(remainingBalance);
            chipAmountButton.setAlpha(isEnabled ? 1.0 : 0.2);
          });

          this.viewModel.setBalanceAmount(remainingBalance);
          this.viewModel.animateChipButtons(!hasBetAmount);
        }
        break;
      default:
        break;
    }
  }
}
